import { modalController, SearchbarCustomEvent, toastController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core';
import { PersonDoc, RoleUtils } from 'common';
import { User } from 'src/global';
import { ConcourseDb } from 'src/global/ConcourseDb';
import { PeopleService, TeamDoc } from 'src/global/service/PeopleService';
import { ConcoursePersonInvite } from './concourse-person-invite';
import { ConcoursePersonUpgrade } from './concourse-person-upgrade';

export interface ConcoursePersonSelectorProps {
  projectId: string;
  feature?: string;
  featureId?: string;
  peopleToOmitIds?: string[];
  multiSelect?: boolean;
  modalTitle?: string;
  needsWrite?: boolean;
}

@Component({
  tag: 'concourse-person-selector',
  styleUrls: ['../../global/app.css', 'concourse-person-selector.css'],
  shadow: true,
})
export class ConcoursePersonSelector {
  static async present(props: ConcoursePersonSelectorProps): Promise<PersonDoc[]> {
    const modal = await modalController.create({
      component: 'concourse-person-selector',
      componentProps: props,
      showBackdrop: true,
      cssClass: 'concourse-modal modal-height-100',
    });
    return new Promise<PersonDoc[]>(resolve => {
      modal.addEventListener('selection', async (e: CustomEvent) => {
        resolve(e.detail as PersonDoc[]);
        modal.dismiss();
      });
      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });

      modal.present();
    });
  }

  @Prop() projectId: string;
  @Prop() peopleToOmitIds: string[] = [];
  @Prop() feature: string;
  @Prop() featureId: string;
  @Prop() needsWrite: boolean = false;
  @Prop() multiSelect: boolean = false;
  @Prop() modalTitle: string = null;
  @State() allTeams: TeamDoc[] = [];
  @State() allPeople: PersonDoc[] = [];
  @State() searchTerm: string = '';
  @State() searchResults: PersonDoc[] = [];
  @State() selectedPeople: PersonDoc[] = [];
  @State() currentTeam: TeamDoc | null = null;
  @State() hideModal: boolean = false;

  @Event() dismiss: EventEmitter;
  @Event() selection: EventEmitter<PersonDoc[]>;

  private peopleDb: ConcourseDb = null;

  get user(): User {
    return globalThis.user;
  }

  async componentWillLoad() {
    this.peopleDb = PeopleService.getPeopleDb(this.projectId);
    this.peopleDb.addListener(ConcourseDb.DOCS_EVENT, this.handlePeopleDocs);
    this.handlePeopleDocs(await this.peopleDb.getDocs());
  }

  // Arrow function automatically binds 'this'
  private handlePeopleDocs = (docs: any[]) => {
    this.allTeams = docs.filter(doc => doc.type == 'team' && !doc.deleted_date);
    this.allPeople = docs.filter(doc => doc.type !== 'team' && !doc.deleted_date);
    // Update search results after data is loaded
    this.updateSearchResults();
  };

  updateSearchResults() {
    let currentPeople = (this.currentTeam ? this.allPeople.filter(person => this.currentTeam.members.includes(person._id)) : this.allPeople).filter(
      person => !this.peopleToOmitIds.includes(person._id),
    );
    if (this.searchTerm.length > 0) {
      this.searchResults = PeopleService.filterPeople(currentPeople, this.searchTerm);
    } else {
      this.searchResults = currentPeople;
    }
  }

  onSearchInput(e: SearchbarCustomEvent) {
    this.searchTerm = e.detail.value.trim();
    if (this.searchTerm.length > 0) {
      if (this.currentTeam) {
        // Search within team members
        const teamMembers = this.allPeople.filter(person => this.currentTeam.members.includes(person._id) && !this.peopleToOmitIds.includes(person._id));
        this.searchResults = PeopleService.filterPeople(teamMembers, this.searchTerm);
      } else {
        // Search in all people
        this.searchResults = PeopleService.filterPeople(this.allPeople, this.searchTerm);
      }
    } else {
      this.updateSearchResults();
    }
  }

  isAdmin(): boolean {
    return RoleUtils.hasProjectWriteRole(this.projectId, this.user.roles);
  }

  private hasPermission(roles: string[]): boolean {
    if (this.needsWrite) {
      return RoleUtils.canWrite(this.projectId, this.feature, this.featureId, roles);
    } else {
      return RoleUtils.canRead(this.projectId, this.feature, this.featureId, roles);
    }
  }

  async selectPerson(person: PersonDoc) {
    if (!this.hasPermission(person.roles)) {
      return this.showUpgradeModal(person);
    }

    if (this.multiSelect) {
      const isSelected = this.selectedPeople.some(p => p._id === person._id);

      if (isSelected) {
        this.selectedPeople = this.selectedPeople.filter(p => p._id !== person._id);
      } else {
        this.selectedPeople = [...this.selectedPeople, person];
      }
    } else {
      // Single select mode - emit selection with single person
      this.selection.emit([person]);
    }
  }

  async showUpgradeModal(person: PersonDoc) {
    this.hideModal = true;
    const response = await ConcoursePersonUpgrade.presentModal({
      projectId: this.projectId,
      person: person,
      feature: this.feature,
      featureId: this.featureId,
      needsWrite: this.needsWrite,
    });
    this.hideModal = false;
    if (response && response.action === 'upgraded') {
      this.selectPerson(response.person);
    } else if (response && response.action === 'requested') {
      toastController
        .create({
          message: `You have requested upgraded access for ${PeopleService.getFullName(person)}. Please wait for the project admin to approve your request.`,
          duration: 3000,
        })
        .then(toast => toast.present());
    }
  }
  isPersonSelected(person: PersonDoc): boolean {
    return this.selectedPeople.some(p => p._id === person._id);
  }

  async selectTeam(team: TeamDoc) {
    this.currentTeam = team;
    this.searchTerm = '';
    this.updateSearchResults();
  }

  goBackToAllPeople() {
    this.currentTeam = null;
    this.searchTerm = '';
    this.updateSearchResults();
  }

  async personInviteHandler() {
    this.hideModal = true;
    const person = await ConcoursePersonInvite.presentModal({
      projectId: this.projectId,
      name: this.searchTerm,
      feature: this.feature,
      featureId: this.featureId,
      needsWrite: this.needsWrite,
    });
    if (person) {
      if (this.multiSelect) {
        this.selection.emit([...this.selectedPeople, person]);
      } else {
        this.selection.emit([person]);
      }
    } else {
      this.hideModal = false;
    }
  }

  async confirmSelection() {
    this.selection.emit(this.selectedPeople);
  }

  async disconnectedCallback() {
    if (this.peopleDb) {
      this.peopleDb.removeListener(ConcourseDb.DOCS_EVENT, this.handlePeopleDocs);
      this.peopleDb = null;
    }
  }

  onModalSearchInput(searchValue: string) {
    this.searchTerm = searchValue;
    if (this.searchTerm.length > 0) {
      if (this.currentTeam) {
        // Search within team members
        const teamMembers = this.allPeople.filter(person => this.currentTeam.members.includes(person._id) && !this.peopleToOmitIds.includes(person._id));
        this.searchResults = PeopleService.filterPeople(teamMembers, this.searchTerm);
      } else {
        // Search in all people
        this.searchResults = PeopleService.filterPeople(this.allPeople, this.searchTerm);
      }
    } else {
      this.updateSearchResults();
    }
  }

  getPersonCard(person: PersonDoc) {
    const iconSrc = PeopleService.getIcon(person);
    const iconIsPhoto = iconSrc.startsWith('data:');
    return (
      <selector-item
        class="selector-item ion-display-flex ion-flex-column"
        selected={this.isPersonSelected(person)}
        showCheckmark={this.multiSelect}
        onItemClick={() => this.selectPerson(person)}
      >
        {/* <div class="icon">{iconIsPhoto ? <img src={iconSrc} /> : <ion-icon src={iconSrc} aria-hidden="true" />}</div> */}
        <div style={{ position: 'relative' }}>
          <concourse-avatar size="medium" initials={iconSrc.length === 1 ? iconSrc : null} imageUrl={iconSrc.length > 1 ? iconSrc : null} border={false} />
        </div>
        <div class="info">
          <h3>{PeopleService.getFullName(person)}</h3>
          <p>
            {person.company_role} - {person.company}
          </p>
        </div>
      </selector-item>
    );
  }

  render() {
    if (this.hideModal) {
      return null;
    }
    const filteredTeams = this.allTeams.filter(team => !this.searchTerm || team.name.toLowerCase().includes(this.searchTerm.toLowerCase()));

    return (
      <Host class="concourse-modal">
        <modal-header
          modalTitle={this.currentTeam ? this.currentTeam.name : this.modalTitle || 'Select People'}
          enableSearch={true}
          searchPlaceholder="Search People"
          searchValue={this.searchTerm}
          onSearchInput={(e: CustomEvent<string>) => this.onModalSearchInput(e.detail)}
          onDismiss={() => this.dismiss.emit()}
          showBack={!!this.currentTeam}
          onBack={this.currentTeam ? () => this.goBackToAllPeople() : () => {}}
        />
        <div class="cc-scrollbars modal-content concourse-person-selector ion-display-flex ion-flex-column">
          {!this.currentTeam && (
            <Fragment>
              {/* Show teams at the top when not in a team view */}
              {filteredTeams.length > 0 && (
                <div class="teams-section">
                  <p class="ion-padding-horizontal">Teams</p>
                  {filteredTeams.map(team => (
                    <div class="selector-item cc-padding" onClick={() => this.selectTeam(team)}>
                      <div class="icon">
                        <ion-icon src="/assets/icon/teams-icon.svg" size="large"></ion-icon>
                      </div>
                      <div class="info">
                        <h3>{team.name}</h3>
                        <p>
                          {team.members.length} member{team.members.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <ion-icon name="chevron-forward-outline" class="team-arrow"></ion-icon>
                    </div>
                  ))}
                </div>
              )}

              {/* Show people */}
              {this.searchResults.length == 0 && filteredTeams.length === 0 && (
                <div>
                  <p>No results found</p>
                  <ion-button shape="round" onClick={() => this.personInviteHandler()}>
                    Invite Them
                  </ion-button>
                </div>
              )}
              {this.searchResults.length > 0 && (
                <div>
                  <p class="ion-padding-horizontal">People</p>
                  {this.searchResults.map(person => this.getPersonCard(person))}
                </div>
              )}
            </Fragment>
          )}

          {/* Show team members when in team view */}
          {this.currentTeam && (
            <Fragment>
              {this.searchResults.length == 0 && (
                <div>
                  <p>No team members found</p>
                </div>
              )}
              {this.searchResults.map(person => this.getPersonCard(person))}
            </Fragment>
          )}
        </div>
        <ion-footer>
          <ion-toolbar>
            <ion-button slot="start" fill="outline" shape="round" onClick={() => this.personInviteHandler()}>
              <ion-icon slot="start" name="add-outline"></ion-icon>
              Invite New
            </ion-button>
            {this.multiSelect && (
              <ion-button
                slot="end"
                fill="solid"
                shape="round"
                data-testid="confirm-selection-button"
                disabled={this.selectedPeople.length === 0}
                onClick={() => this.confirmSelection()}
              >
                Confirm ({this.selectedPeople.length})
              </ion-button>
            )}
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
